import React, { useState, useEffect } from 'react';
import {
  Modal, Form, DatePicker, Input, Button, Space, Typography, Alert, Card,
  Spin, message, Row, Col, Checkbox, Badge, Tag, Divider, Descriptions
} from 'antd';
import {
  CalendarOutlined, ClockCircleOutlined, WarningOutlined,
  CheckCircleOutlined, StarOutlined, UserOutlined
} from '@ant-design/icons';
import { appointmentApi, Appointment, AvailableTimeSlot } from '../../services/appointmentApi';
import { getTokens } from '../../services/authApi';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface RescheduleModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const RescheduleModal: React.FC<RescheduleModalProps> = ({
  visible,
  appointment,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState<AvailableTimeSlot[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [conflicts, setConflicts] = useState<any[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlot | null>(null);

  useEffect(() => {
    if (visible && appointment) {
      loadSuggestions();
      form.setFieldsValue({
        new_start_time: dayjs(appointment.start_time),
        notify_customer: true,
        manual_override: false
      });
    }
  }, [visible, appointment]);

  const loadSuggestions = async () => {
    if (!appointment?.id) return;

    setLoadingSuggestions(true);
    try {
      const result = await appointmentApi.getRescheduleOptions(appointment.id, {
        max_results: 10
      });
      setSuggestions(result.suggested_slots || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      message.error('Failed to load suggested time slots');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSlotSelect = (slot: AvailableTimeSlot) => {
    setSelectedSlot(slot);
    form.setFieldValue('new_start_time', dayjs(slot.start_time));
    message.success(`Selected ${dayjs(slot.start_time).format('MMM DD, YYYY @ h:mm A')}`);
  };

  const handleSubmit = async (values: any) => {
    if (!appointment?.id) return;

    setSubmitting(true);
    try {
      // TODO: Get actual user ID from auth context
      const userId = 1; // Default to admin user

      const result = await appointmentApi.reschedule(appointment.id, {
        new_start_time: values.new_start_time.toISOString(),
        duration_minutes: appointment.duration_minutes,
        reason: values.reason,
        notify_customer: values.notify_customer !== false,
        changed_by_user_id: userId,
        manual_override: values.manual_override || false,
        override_reason: values.override_reason
      });

      message.success('Appointment rescheduled successfully');
      form.resetFields();
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error rescheduling appointment:', error);

      if (error.response?.status === 409) {
        // Conflict detected
        const conflictData = error.response.data;
        setConflicts(conflictData.conflicts || []);
        setShowConflicts(true);

        if (conflictData.can_override) {
          message.warning('Conflicts detected. You can enable manual override to proceed.');
        } else {
          message.error('Cannot reschedule due to critical conflicts. Please choose a different time.');
        }
      } else {
        message.error('Failed to reschedule appointment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setConflicts([]);
    setShowConflicts(false);
    setSelectedSlot(null);
    onClose();
  };

  if (!appointment) return null;

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          <span>Reschedule Appointment</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={900}
      footer={null}
    >
      <Row gutter={16}>
        {/* Current Appointment Info */}
        <Col span={24}>
          <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Current Date">
                {dayjs(appointment.start_time).format('MMMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Current Time">
                {dayjs(appointment.start_time).format('h:mm A')} - {dayjs(appointment.end_time).format('h:mm A')}
              </Descriptions.Item>
              <Descriptions.Item label="Appointment">
                {appointment.title}
              </Descriptions.Item>
              <Descriptions.Item label="Technician">
                {appointment.technician_name || 'Unknown'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Left Column - Form */}
        <Col span={12}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Title level={5}>New Schedule</Title>

            <Form.Item
              label="New Start Time"
              name="new_start_time"
              rules={[{ required: true, message: 'Please select new start time' }]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                minuteStep={15}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>

            <Form.Item
              label="Reason for Rescheduling"
              name="reason"
            >
              <TextArea
                rows={3}
                placeholder="e.g., Customer requested, parts delay, technician unavailable..."
              />
            </Form.Item>

            <Form.Item
              name="notify_customer"
              valuePropName="checked"
            >
              <Checkbox defaultChecked>
                Notify customer about reschedule
              </Checkbox>
            </Form.Item>

            {/* Conflicts Alert */}
            {showConflicts && conflicts.length > 0 && (
              <>
                <Alert
                  message="Scheduling Conflicts Detected"
                  description={
                    <div>
                      {conflicts.map((conflict, index) => (
                        <div key={index}>
                          <Tag color={
                            conflict.conflict_severity === 'critical' ? 'red' :
                            conflict.conflict_severity === 'high' ? 'orange' :
                            conflict.conflict_severity === 'medium' ? 'gold' : 'blue'
                          }>
                            {conflict.conflict_severity}
                          </Tag>
                          {conflict.description}
                        </div>
                      ))}
                    </div>
                  }
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginBottom: 16 }}
                />

                <Form.Item
                  name="manual_override"
                  valuePropName="checked"
                >
                  <Checkbox>
                    Override conflicts (I understand the risks)
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.manual_override !== currentValues.manual_override
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue('manual_override') ? (
                      <Form.Item
                        label="Override Reason"
                        name="override_reason"
                        rules={[{ required: true, message: 'Please provide override reason' }]}
                      >
                        <TextArea
                          rows={2}
                          placeholder="Explain why you're overriding the conflict..."
                        />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </>
            )}

            <Divider />

            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<CheckCircleOutlined />}
                loading={submitting}
              >
                Reschedule Appointment
              </Button>
            </Space>
          </Form>
        </Col>

        {/* Right Column - Suggestions */}
        <Col span={12}>
          <Title level={5}>
            <StarOutlined /> Suggested Time Slots
          </Title>

          {loadingSuggestions ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin tip="Finding best time slots..." />
            </div>
          ) : suggestions.length === 0 ? (
            <Alert
              message="No suggestions available"
              description="Try selecting a different date range"
              type="info"
              showIcon
            />
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: 8 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {suggestions.map((slot, index) => (
                  <Card
                    key={index}
                    size="small"
                    hoverable
                    onClick={() => handleSlotSelect(slot)}
                    style={{
                      cursor: 'pointer',
                      borderColor: selectedSlot === slot ? '#1890ff' : undefined,
                      background: selectedSlot === slot ? '#e6f7ff' : undefined
                    }}
                  >
                    {slot.recommended && (
                      <Tag
                        color="gold"
                        icon={<StarOutlined />}
                        style={{ marginBottom: 8 }}
                      >
                        Recommended
                      </Tag>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div>
                          <UserOutlined /> <Text strong>{slot.technician_name}</Text>
                        </div>
                        <div>
                          <ClockCircleOutlined /> {dayjs(slot.start_time).format('MMM DD @ h:mm A')}
                        </div>
                      </div>
                      {slot.available ? (
                        <Badge status="success" text="Available" />
                      ) : (
                        <Badge status="error" text="Conflict" />
                      )}
                    </div>
                    {slot.reason && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {slot.reason}
                      </Text>
                    )}
                  </Card>
                ))}
              </Space>
            </div>
          )}
        </Col>
      </Row>
    </Modal>
  );
};
